import { type Anchor, Col, Layer, Sheet, Tappable, Text, useTheme, type ViewHandle } from "@rally/ui-kit";
import { useRef, useState } from "react";
import { say } from "../../localization/say.ts";
import { act } from "../../net/rally.ts";
import { LANE_WIDTH } from "../reading.ts";
import { afterToggling, pressWouldRemove, type Reaction, type Target } from "./reacting.ts";
import { Chip, MoreChip } from "./reaction-chip.tsx";
import { holdingOrder, inOrder, planRow } from "./reaction-order.ts";
import { Add, ReactionPicker, whereItIs } from "./reaction-picker.tsx";
import { ReactionSheet } from "./reaction-sheet.tsx";

export function Lane({
  shown,
  onReact,
  testID,
}: {
  shown: boolean;
  onReact: (at: Anchor) => void;
  testID: string;
}) {
  const theme = useTheme();
  const box = useRef<ViewHandle | null>(null);
  return (
    <Col
      testID={testID}
      style={{
        width: LANE_WIDTH,
        flexDirection: "row",
        justifyContent: "flex-end",
        alignItems: "flex-start",
        gap: theme.space[1],
        opacity: shown ? 1 : 0,
      }}
      {...(shown ? {} : { "aria-hidden": true, pointerEvents: "none" as const })}
    >
      <Tappable
        ref={box}
        onPress={() => whereItIs(box.current, onReact)}
        accessibilityRole="button"
        accessibilityLabel={say("react.add")}
        testID={`${testID}-react`}
        style={{ width: 28, height: 28, alignItems: "center", justifyContent: "center" }}
      >
        <Text style={{ fontSize: 15 }}>🙂</Text>
      </Tappable>
    </Col>
  );
}

export type ReactedProps = {
  target: Target;

  reactions: readonly Reaction[];

  hovered: boolean;

  phone: boolean;

  picking: Anchor | null;
  onPicking: (at: Anchor | null) => void;

  onChanged?: () => void;
  onError?: (cause: unknown) => void;

  add?: boolean;
  testID?: string;
};

export function Reacted({
  target,
  reactions,
  hovered,
  phone,
  picking,
  onPicking,
  onChanged,
  onError,
  add = true,
  testID = "reacted",
}: ReactedProps) {
  const theme = useTheme();

  const [pressed, setPressed] = useState<Reaction[] | null>(null);
  const [lastRead, setLastRead] = useState<readonly Reaction[]>(reactions);
  if (lastRead !== reactions) {
    setLastRead(reactions);
    setPressed(null);
  }

  const [held, setHeld] = useState<string[]>(() => inOrder(reactions).map((one) => one.emoji));

  const [expanded, setExpanded] = useState(false);

  const [naming, setNaming] = useState(false);

  const [reading, setReading] = useState<string | null>(null);

  const shown = pressed ?? reactions;

  const open = reading !== null || expanded || naming || picking !== null;
  const ordered = open ? holdingOrder(held, shown) : inOrder(shown);

  const spelled = ordered.map((one) => one.emoji);
  if (spelled.join(" ") !== held.join(" ")) setHeld(spelled);
  const plan = planRow(ordered, expanded);

  const toggle = (emoji: string) => {
    const removing = pressWouldRemove(shown, emoji);
    setPressed(afterToggling(shown, emoji));
    void act(removing ? "react.remove" : "react.add", { target, emoji })
      .then(() => onChanged?.())

      .catch((cause) => {
        setPressed(null);
        onError?.(cause);
      });
  };

  const chips = ordered.length > 0;

  const addShown = phone || hovered || picking !== null;

  return (
    <Col style={{ gap: theme.space[1], overflow: "visible" }} testID={testID}>
      {chips ? (
        <Col
          style={{
            flexDirection: "row",
            flexWrap: "wrap",
            alignItems: "center",
            gap: theme.space[2],

            overflow: "visible",
          }}
        >
          {plan.shown.map((one) => (
            <Chip
              key={one.emoji}
              reaction={one}
              onPress={() => toggle(one.emoji)}
              onHold={phone ? () => setNaming(true) : undefined}
              onReading={(on) => setReading((was) => (on ? one.emoji : was === one.emoji ? null : was))}
              testID={`${testID}-pill-${one.emoji}`}
            />
          ))}
          {plan.more > 0 ? (
            <MoreChip
              many={plan.more}
              onPress={() => (plan.opens === "sheet" ? setNaming(true) : setExpanded(true))}
              testID={`${testID}-more`}
            />
          ) : null}
          {add ? (
            <Add shown={addShown} testID={`${testID}-add`} onPress={(at) => onPicking(picking ? null : at)} />
          ) : null}
        </Col>
      ) : null}
      {picking && !phone ? (
        <Layer anchor={picking} width={264} align="end" side="bottom" onDismiss={() => onPicking(null)}>
          <ReactionPicker testID={`${testID}-picker`} onPick={toggle} onDismiss={() => onPicking(null)} />
        </Layer>
      ) : null}
      {phone ? (
        <Sheet
          visible={picking !== null}
          onDismiss={() => onPicking(null)}
          title={say("react.add")}
          testID={`${testID}-sheet`}
        >
          <ReactionPicker testID={`${testID}-picker`} onPick={toggle} onDismiss={() => onPicking(null)} />
        </Sheet>
      ) : null}
      <ReactionSheet
        visible={naming}
        reactions={ordered}
        onDismiss={() => setNaming(false)}
        testID={`${testID}-who-sheet`}
      />
    </Col>
  );
}
